import { useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, useParams } from "react-router";
import { ENGINE_VERSION, MasterDataSchema, ScenarioSchema, ACTIVITY_KIND_LABEL, EXCLUSION_LABEL, LOCATION_LABEL, METHOD_LABEL, STAGE_LABEL, VALUE_TYPE_LABEL, fmt, pct, type MasterData, type CalcResult, type EfApplied, type Scenario, type ValueType } from "@h2lca/core";
import { ReportBars } from "@/components/charts/ReportBars";
import { Wordmark } from "@/components/brand/Brand";
import { ButtonLink, Button } from "@/components/ui/FormControls";
import { DENOMINATOR_LABEL, H2_STATE_LABEL, ISSUE_LEVEL_LABEL, efStatusLabel, formatDateTime } from "@/components/workshop/helpers";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { hasFeature } from "@/lib/license";
import { useAuth } from "@/store/auth";
import { useResult } from "@/store/useResult";
import { useMaster } from "@/store/master";
import { useWorkshop } from "@/store/workshop";

const DISCLAIMER = "본 결과는 모의산정 결과로서 종합적인 환경영향평가, 제3자 검증 또는 청정수소 인증기관의 공식 판정을 대체하지 않습니다.";

/**
 * 결과보고서를 화면에서 확인하고 브라우저 인쇄 기능으로 PDF로 저장한다. AppShell 을 쓰지 않고 얇은 막대만 두어 인쇄 때 감춘다.
 * 조판은 globals.css 의 report-preview / print-doc pd-* 가 화면과 종이 양쪽을 맡는다.
 */
export function ReportPage() {
  const scenario = useWorkshop((s) => s.scenario);
  const restoreDraft = useWorkshop((s) => s.restoreDraft);
  const result = useResult();
  const data = useMaster((s) => s.data);
  const [tried, setTried] = useState(false);
  useEffect(() => {
    if (!useWorkshop.getState().scenario) restoreDraft();
    setTried(true);
  }, [restoreDraft]);
  if (!scenario || !result) return tried ? <Navigate to="/app" replace /> : null;

  return <ReportView scenario={scenario} result={result} data={data} />;
}

interface ReportRun {
  id: string;
  createdAt: string;
  engineVersion: string;
  masterFingerprint: string;
  inputSnapshot: Scenario;
  result: CalcResult;
  masterSnapshot: { data: MasterData };
}

/** 저장 당시 입력·결과·마스터를 함께 사용하며 현재 초안이나 마스터로 재계산하지 않는다. */
export function SavedRunReportPage() {
  const { projectId, runId } = useParams();
  const [run, setRun] = useState<ReportRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const backTo = `/workspace/projects/${projectId}`;
  useEffect(() => {
    let cancelled = false;
    setRun(null); setError(null);
    void api<{ run: ReportRun }>(`/workspace/projects/${projectId}/runs/${runId}`).then(({ run: saved }) => {
      if (saved.engineVersion !== ENGINE_VERSION) throw new Error("이전 엔진의 기록입니다. 프로젝트에서 현재 기준으로 다시 실행한 뒤 보고서를 확인하세요.");
      const inputSnapshot = ScenarioSchema.parse(saved.inputSnapshot);
      const data = MasterDataSchema.parse(saved.masterSnapshot.data);
      if (!cancelled) setRun({ ...saved, inputSnapshot, masterSnapshot: { data } });
    }).catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "실행 보고서를 불러오지 못했습니다."); });
    return () => { cancelled = true; };
  }, [projectId, runId]);
  if (!run) return <main className="page flex flex-col gap-section"><h1>실행 기록 보고서</h1><p role={error ? "alert" : "status"} className={error ? "note note-warn" : "text-ink-muted"}>{error ?? "저장된 실행 기록을 불러오는 중…"}</p><div><ButtonLink to={backTo}>프로젝트로 돌아가기</ButtonLink></div></main>;
  return <ReportView scenario={run.inputSnapshot} result={run.result} data={run.masterSnapshot.data} run={run} backTo={backTo} />;
}

function ReportView({ scenario, result, data, run, backTo = "/app/work" }: { scenario: Scenario; result: CalcResult; data: MasterData; run?: ReportRun; backTo?: string }) {
  const userLicense = useAuth((s) => s.user?.license);
  const pdfAvailable = hasFeature("report_pdf", userLicense);
  const [pdfNoticeOpen, setPdfNoticeOpen] = useState(false);
  const backLabel = run ? "프로젝트로 돌아가기" : "산정 화면으로";
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
  return (
    <div className="min-h-dvh bg-canvas">
      <header className="chrome-bar print:hidden">
        <div className="mx-auto flex w-full max-w-app items-center gap-content px-section py-item">
          <Link to={backTo} className="inline-flex" aria-label={backLabel}>
            <Wordmark />
          </Link>
          <span className="text-caption text-ink-muted">결과보고서</span>
          <div className="ml-auto flex items-center gap-item">
            <ButtonLink to={backTo} variant="secondary">
              {backLabel}
            </ButtonLink>
            <Button
              variant={pdfAvailable ? "primary" : "secondary"}
              aria-expanded={pdfAvailable ? undefined : pdfNoticeOpen}
              onClick={() => {
                if (pdfAvailable) window.print();
                else setPdfNoticeOpen((value) => !value);
              }}
            >
              PDF 저장
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-report px-section py-page print:p-0">
        {!pdfAvailable && pdfNoticeOpen && (
          <div className="report-license-notice print:hidden" role="status">
            <div className="report-license-notice-copy">
              <strong>PDF 저장은 기업용 기능입니다</strong>
              <p>기업용 계정에서 브라우저 인쇄 창을 통해 결과 보고서를 PDF 파일로 저장할 수 있습니다.</p>
            </div>
            <Button type="button" size="compact" onClick={() => setPdfNoticeOpen(false)}>닫기</Button>
          </div>
        )}
        <div className="report-preview">
          <ReportDoc scenario={scenario} result={result} data={data} run={run} />
        </div>
      </main>
    </div>
  );
}

function ReportTable({ children }: { children: ReactNode }) {
  return <div className="report-table-scroll"><table>{children}</table></div>;
}

function ReportDoc({ scenario, result, data, run }: { scenario: Scenario; result: CalcResult; data: MasterData; run?: ReportRun }) {
  const user = useAuth((s) => s.user);
  const tech = data.technologies.find((t) => t.id === scenario.basis.productionTechId);
  const method = data.methods.find((m) => m.id === scenario.method);
  const example = scenario.exampleId ? data.examples.find((e) => e.id === scenario.exampleId) : undefined;
  const ordered = [...result.byEquipment].sort((a, b) => a.order - b.order);
  const fu = scenario.functionalUnit;

  // 적용 배출계수 , 같은 계수는 한 번만
  const efs = new Map<string, EfApplied>();
  for (const e of result.byEquipment) for (const l of e.lines) if (l.ef) efs.set(l.ef.id, l.ef);

  for (const source of result.electricity.sources) if (source.factor) efs.set(source.factor.id, source.factor);

  return (
    <article className="print-doc">
      <div className="pd-head">
        <h1>{scenario.name}</h1>
        <p className="pd-sub">수소 생산, 저장, 이송 온실가스 배출량 산정 결과보고서 (모의산정)</p>
        <p className="pd-meta">
          {BRAND.TITLE}, {run ? "실행" : "입력 수정"} {formatDateTime(run?.createdAt ?? scenario.meta.updatedAt)}
          {!run && user && `, ${user.name} (${user.organization})`}
        </p>
      </div>

      <p className="pd-note">{run ? `실행 ID: ${run.id} · 마스터 데이터 버전: ${run.masterFingerprint}` : `현재 작업의 미리보기 보고서${user?.license === "enterprise" ? " · 저장된 실행 기록 보고서는 프로젝트에서 확인할 수 있습니다." : ""}`} · 엔진 {run?.engineVersion ?? ENGINE_VERSION}</p>
      <div className="pd-grid">
        <div className="pd-cell">
          <span>1 kg당 배출량</span>
          <b>{result.perKgH2 === null ? "계산 제한" : `${fmt(result.perKgH2, 3)} kgCO2eq/kgH2`}</b>
        </div>
        <div className="pd-cell">
          <span>총 온실가스 배출량</span>
          <b>{fmt(result.totalKgCO2eq)} kgCO2eq</b>
        </div>
        <div className="pd-cell">
          <span>분모: {DENOMINATOR_LABEL[result.denominator.kind]}</span>
          <b>{fmt(result.denominator.kg)} kg</b>
        </div>
        <div className="pd-cell">
          <span>생산방식</span>
          <b>{tech?.name ?? scenario.basis.productionTechId}</b>
        </div>
        <div className="pd-cell">
          <span>산정방식</span>
          <b>{METHOD_LABEL[scenario.method]}</b>
        </div>
        <div className="pd-cell">
          <span>산정 상태</span>
          <b>{result.status === "ok" ? "계산 가능" : `계산 제한 (오류 ${result.issues.filter((i) => i.level === "error").length})`}</b>
        </div>
      </div>
      <p className="pd-note">기능단위 수소 {fmt(result.functionalUnitKg)} kg의 배출량: {result.perFunctionalUnitKgCO2eq == null ? "계산 제한" : `${fmt(result.perFunctionalUnitKgCO2eq, 3)} kgCO2eq/FU`}</p>
      <p className="pd-warn">{DISCLAIMER}</p>

      <section>
        <h2>1. 산정목적과 기본조건</h2>
        <ReportTable>
          <tbody>
            <tr>
              <th>산정목적</th>
              <td className="pd-left">{scenario.meta.purpose || "없음"}</td>
            </tr>
            <tr>
              <th>시나리오 기준연도</th>
              <td>{scenario.meta.baseYear}</td>
            </tr>
            <tr>
              <th>생산방식(대상기술)</th>
              <td className="pd-left">{tech?.name ?? scenario.basis.productionTechId}</td>
            </tr>
            <tr>
              <th>산정 기준 수소량</th>
              <td>{fmt(scenario.basis.batchH2Kg)} kg</td>
            </tr>
            {scenario.basis.annualOutputKg !== undefined && (
              <tr>
                <th>연간 생산량(참고)</th>
                <td>{fmt(scenario.basis.annualOutputKg)} kg/년</td>
              </tr>
            )}
            {scenario.basis.operatingHoursPerYear !== undefined && (
              <tr>
                <th>연간 운전시간(참고)</th>
                <td>{fmt(scenario.basis.operatingHoursPerYear)} h/년</td>
              </tr>
            )}
            {example && (
              <tr>
                <th>출발 예시</th>
                <td className="pd-left">{example.name}</td>
              </tr>
            )}
          </tbody>
        </ReportTable>
      </section>

      <section>
        <h2>2. 산정기준</h2>
        <ReportTable>
          <tbody>
            <tr>
              <th>산정방식</th>
              <td className="pd-left">{result.methodLabel}</td>
            </tr>
            <tr>
              <th>적용 표준과 근거</th>
              <td className="pd-left">{result.standards.join("; ")}</td>
            </tr>
            <tr>
              <th>GWP 기준</th>
              <td className="pd-left">{result.gwpBasis}</td>
            </tr>
            <tr>
              <th>시스템 경계 원칙</th>
              <td className="pd-left">{method?.boundary.description ?? "없음"}</td>
            </tr>
            <tr>
              <th>영향범주</th>
              <td className="pd-left">기후변화(온실가스)만 산정</td>
            </tr>
          </tbody>
        </ReportTable>
        {result.notices.length > 0 && (
          <ul>
            {result.notices.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2>3. 기능단위와 기준흐름</h2>
        <ReportTable>
          <tbody>
            <tr>
              <th>기능단위</th>
              <td className="pd-left">
                순도 {fu.purityPct}%, {fu.pressureBar} bar, {H2_STATE_LABEL[fu.state]} 상태로 {fu.deliveryPoint}에 인도되는 수소 {result.functionalUnitKg} kg
              </td>
            </tr>
            <tr>
              <th>생산량 → 출하 → 인도</th>
              <td>
                {fmt(result.batch.producedKg)} → {fmt(result.batch.gateKg)} → {fmt(result.batch.deliveredKg)} kg (손실 {fmt(result.batch.lossKg)} kg)
              </td>
            </tr>
            <tr>
              <th>기준흐름</th>
              <td>{result.denominator.kg > 0 ? `${fmt((result.batch.producedKg / result.denominator.kg) * result.functionalUnitKg, 4)} kg 생산 / 인도 ${result.functionalUnitKg} kg` : "없음"}</td>
            </tr>
          </tbody>
        </ReportTable>
      </section>

      <section>
        <h2>4. 설비 구성과 시스템 경계</h2>
        <ReportTable>
          <thead>
            <tr>
              <th>순서</th>
              <th className="pd-left">단계</th>
              <th className="pd-left">설비</th>
              <th className="pd-left">위치</th>
              <th className="pd-left">포함/제외</th>
              <th className="pd-left">사유</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((e) => {
              const eq = scenario.equipment.find((x) => x.id === e.equipmentId);
              return (
                <tr key={e.equipmentId}>
                  <td>{e.order + 1}</td>
                  <td className="pd-left">{STAGE_LABEL[e.stage]}</td>
                  <td className="pd-left">{e.name}</td>
                  <td className="pd-left">{LOCATION_LABEL[eq?.location ?? "onsite"]}</td>
                  <td className="pd-left">{e.inBoundary ? "포함" : "제외"}</td>
                  <td className="pd-left">{e.inBoundary ? "" : `${e.exclusionReason ? EXCLUSION_LABEL[e.exclusionReason] : ""}${eq?.exclusionNote ? ` (${eq.exclusionNote})` : ""}`}</td>
                </tr>
              );
            })}
          </tbody>
        </ReportTable>
        <p className="pd-note">경계 밖 설비는 화면과 목록에 남기되 계산에서 제외했습니다.</p>
        {scenario.basis.technologyReview && <p className="pd-warn">생산기술과 설비 구성 유지 근거: {scenario.basis.technologyReview.note}</p>}
      </section>

      <section className="pd-break">
        <h2>5. 운전자료(활동자료)</h2>
        <ReportTable>
          <thead>
            <tr>
              <th className="pd-left">설비</th>
              <th className="pd-left">항목</th>
              <th className="pd-left">종류</th>
              <th>값</th>
              <th className="pd-left">단위</th>
              <th className="pd-left">값 유형</th>
              <th className="pd-left">출처</th>
            </tr>
          </thead>
          <tbody>
            {ordered.flatMap((e) =>
              e.lines.map((l) => (
                <tr key={l.inputId}>
                  <td className="pd-left">{e.name}</td>
                  <td className="pd-left">{l.label}</td>
                  <td className="pd-left">{ACTIVITY_KIND_LABEL[l.kind]}</td>
                  <td>{l.amount === null ? "없음" : fmt(l.amount, 3)}</td>
                  <td className="pd-left">{l.unit}</td>
                  <td className="pd-left">{VALUE_TYPE_LABEL[l.valueType as ValueType] ?? l.valueType}</td>
                  <td className="pd-left">{l.source ?? ""}</td>
                </tr>
              )),
            )}
            {ordered.map((e) =>
              e.h2.outKg !== null || e.h2.inKg !== null ? (
                <tr key={`${e.equipmentId}-h2`}>
                  <td className="pd-left">{e.name}</td>
                  <td className="pd-left">수소 흐름</td>
                  <td className="pd-left">투입 / 산출 / 손실</td>
                  <td>
                    {e.h2.inKg === null ? "없음" : fmt(e.h2.inKg)} / {e.h2.outKg === null ? "없음" : fmt(e.h2.outKg)} / {e.h2.lossKg === null ? "없음" : fmt(e.h2.lossKg)}
                  </td>
                  <td className="pd-left">kg</td>
                  <td className="pd-left" />
                  <td className="pd-left" />
                </tr>
              ) : null,
            )}
          </tbody>
        </ReportTable>
        <h3>전력 믹스</h3>
        <ReportTable>
          <thead>
            <tr>
              <th className="pd-left">전력원</th>
              <th>비율</th>
              <th>배출계수 (kgCO2eq/kWh)</th>
              <th>전력량 (kWh)</th>
            </tr>
          </thead>
          <tbody>
            {result.electricity.sources.map((s) => (
              <tr key={s.sourceId}>
                <td className="pd-left">{s.label}</td>
                <td>{pct(s.share)}</td>
                <td>{fmt(s.ef, 4)}</td>
                <td>{fmt(s.kWh)}</td>
              </tr>
            ))}
            <tr className="pd-total">
              <td className="pd-left">합성 계수</td>
              <td>100%</td>
              <td>{fmt(result.electricity.mixEf, 4)}</td>
              <td>{fmt(result.electricity.totalKWh)}</td>
            </tr>
          </tbody>
        </ReportTable>
        <p className="pd-note">전력원 입력 합계: {result.electricity.enteredKWh == null ? "비율 입력" : `${fmt(result.electricity.enteredKWh)} kWh`}. 전력원 입력량은 비율 산정에만 사용하고, 배출량에는 설비 전력량을 적용합니다. 측정기간·경계와 차이 근거: {scenario.electricity.note || "미기록"}</p>
        <p className="pd-note">부산물 배분: {result.allocation.note}</p>
      </section>

      <section>
        <h2>6. 배출계수</h2>
        <ReportTable>
          <thead>
            <tr>
              <th className="pd-left">항목</th>
              <th className="whitespace-nowrap">값</th>
              <th className="pd-left">단위</th>
              <th className="pd-left">출처</th>
              <th className="whitespace-nowrap">원본 대표연도</th>
              <th className="pd-left">상태</th>
            </tr>
          </thead>
          <tbody>
            {[...efs.values()].map((f) => (
              <tr key={f.id}>
                <td className="pd-left">{f.name}<br />{f.id}</td>
                <td className="whitespace-nowrap">{fmt(f.value, 4)}</td>
                <td className="pd-left whitespace-nowrap">{f.unit}</td>
                <td className="pd-left">{f.source}{f.sourceUrl && <><br />{f.sourceUrl}</>}<br />GWP: {f.gwpBasis || (f.composed ? "구성 계수 참조" : "미기록")}</td>
                <td>{f.baseYear > 0 ? f.baseYear : f.composed ? "구성 계수 참조" : "미기록"}</td>
                <td className="pd-left">{efStatusLabel(f.status).label}</td>
              </tr>
            ))}
            {efs.size === 0 && (
              <tr>
                <td className="pd-left" colSpan={6}>
                  적용된 배출계수가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </ReportTable>
        <p className="pd-note">시나리오 기준연도 {scenario.meta.baseYear}년과 배출계수 원본 대표연도는 서로 다릅니다. 합성 계수에는 구성 계수의 출처를 적용합니다. GWP 기준: {result.gwpBasis}. 임시값(전문가 검토 전)은 착수보고와 전문가 자문 후 확정값으로 교체됩니다.</p>
      </section>

      <section className="pd-break">
        <h2>7. 계산과정</h2>
        {result.steps.map((st, i) => (
          <div key={`${st.title}-${i}`}>
            <h3>{st.title}</h3>
            <ul className="pd-flow">
              {st.lines.map((l, j) => (
                <li key={j}>{l}</li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      <section>
        <h2>8. 결과</h2>
        <ReportTable>
          <tbody>
            <tr>
              <th>총 온실가스 배출량(배분 전)</th>
              <td>{fmt(result.grossKgCO2eq)} kgCO2eq</td>
            </tr>
            <tr>
              <th>총 온실가스 배출량</th>
              <td>{fmt(result.totalKgCO2eq)} kgCO2eq</td>
            </tr>
            <tr>
              <th>{DENOMINATOR_LABEL[result.denominator.kind]}</th>
              <td>{fmt(result.denominator.kg)} kg</td>
            </tr>
            <tr className="pd-total">
              <td className="pd-left">1 kg당 배출량</td>
              <td>{result.perKgH2 === null ? "계산 제한" : `${fmt(result.perKgH2, 3)} kgCO2eq/kgH2`}</td>
            </tr>
          </tbody>
        </ReportTable>

        <h3>단계별 배출량</h3>
        <ReportTable>
          <thead>
            <tr>
              <th className="pd-left">단계</th>
              <th>kgCO2eq</th>
              <th>비율</th>
            </tr>
          </thead>
          <tbody>
            {result.byStage.map((s) => (
              <tr key={s.stage}>
                <td className="pd-left">{STAGE_LABEL[s.stage]}</td>
                <td>{fmt(s.kgCO2eq)}</td>
                <td>{pct(s.share)}</td>
              </tr>
            ))}
          </tbody>
        </ReportTable>
        <figure className="pd-chart">
          <figcaption>단계별 배출량 (kgCO2eq)</figcaption>
          <ReportBars rows={result.byStage.map((s) => ({ label: STAGE_LABEL[s.stage], value: s.kgCO2eq, stage: s.stage }))} />
        </figure>

        <h3>설비별 배출량</h3>
        <ReportTable>
          <thead>
            <tr>
              <th className="pd-left">설비</th>
              <th className="pd-left">단계</th>
              <th>kgCO2eq</th>
              <th>비율</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((e) => (
              <tr key={e.equipmentId}>
                <td className="pd-left">
                  {e.name}
                  {!e.inBoundary && " (제외)"}
                </td>
                <td className="pd-left">{STAGE_LABEL[e.stage]}</td>
                <td>{e.inBoundary ? fmt(e.kgCO2eq) : "없음"}</td>
                <td>{e.inBoundary ? pct(e.share) : "없음"}</td>
              </tr>
            ))}
          </tbody>
        </ReportTable>
        <figure className="pd-chart">
          <figcaption>설비별 배출량 (kgCO2eq, 경계 안 설비)</figcaption>
          <ReportBars rows={ordered.filter((e) => e.inBoundary).map((e) => ({ label: e.name, value: e.kgCO2eq, stage: e.stage }))} />
        </figure>

        {result.cert && (
          <>
            <h3>청정수소 인증제 모의 판정</h3>
            <ReportTable>
              <tbody>
                <tr>
                  <th>적용조건</th>
                  <td className="pd-left">{result.cert.eligible ? "충족" : `미충족: ${result.cert.reasons.join("; ")}`}</td>
                </tr>
                <tr>
                  <th>모의 등급</th>
                  <td>{result.cert.grade !== null ? `${result.cert.grade}등급` : "해당 없음"}</td>
                </tr>
                <tr>
                  <th>청정수소 기준</th>
                  <td>{result.cert.thresholdKgCO2eq !== null ? `${result.cert.thresholdKgCO2eq} kgCO2eq/kgH2 이하` : "없음"}</td>
                </tr>
                <tr>
                  <th>재생에너지 비율(단순화)</th>
                  <td>{pct(result.cert.renewableShare)}</td>
                </tr>
              </tbody>
            </ReportTable>
          </>
        )}

        {example?.reference && (
          <>
            <h3>기준결과값 비교</h3>
            <ReportTable>
              <tbody>
                <tr>
                  <th>기준결과값</th>
                  <td>{fmt(example.reference.perKgH2, 3)} kgCO2eq/kgH2</td>
                </tr>
                <tr>
                  <th>계산값</th>
                  <td>{result.perKgH2 === null ? "없음" : `${fmt(result.perKgH2, 3)} kgCO2eq/kgH2`}</td>
                </tr>
                <tr>
                  <th>차이</th>
                  <td>{result.perKgH2 === null ? "없음" : `${fmt(result.perKgH2 - example.reference.perKgH2, 3)} kgCO2eq/kgH2`}</td>
                </tr>
              </tbody>
            </ReportTable>
          </>
        )}
      </section>

      <section>
        <h2>9. 검증 메시지</h2>
        {result.issues.length === 0 ? (
          <p className="pd-note">검증 메시지가 없습니다.</p>
        ) : (
          <ReportTable>
            <thead>
              <tr>
                <th className="pd-left">수준</th>
                <th className="pd-left">내용</th>
              </tr>
            </thead>
            <tbody>
              {result.issues.map((i, k) => (
                <tr key={`${i.code}-${k}`}>
                  <td className="pd-left">{ISSUE_LEVEL_LABEL[i.level]}</td>
                  <td className="pd-left">{i.message}</td>
                </tr>
              ))}
            </tbody>
          </ReportTable>
        )}
      </section>

      <section>
        <h2>10. 안내</h2>
        <ul>
          <li>{DISCLAIMER}</li>
          <li>본 프로그램은 기후변화 영향범주(온실가스)만 다루며, 산성화, 부영양화 등 다른 영향범주는 산정대상이 아닙니다.</li>
          <li>기본값과 배출계수 중 ‘임시값(전문가 검토 전)’으로 표시된 값은 전문가 검토 후 교체될 수 있습니다.</li>
          <li>용어 정의는 KS I ISO 14040:2006 및 KS I ISO 14044:2006을 바탕으로 합니다.</li>
        </ul>
        <p className="pd-foot">
          {BRAND.TITLE}, {BRAND.ORG}, 개발 {BRAND.DEVELOPER}
        </p>
      </section>
    </article>
  );
}
